import { useState } from 'react';
import { ChevronDown, ExternalLink } from 'lucide-react';
import { SectionLabel } from './ui';

const fmtVal = (f) => {
  if (f.value == null) return '—';
  const v = Number(f.value);
  const digits = Math.abs(v) >= 100 ? 2 : Math.abs(v) >= 10 ? 2 : v % 1 === 0 ? 2 : 4;
  const num = f.key === 'gap' ? `${v >= 0 ? '+' : ''}${v.toFixed(2)}` : v.toLocaleString('en-CA', { maximumFractionDigits: digits, minimumFractionDigits: f.unit === '%' ? 2 : 0 });
  return `${num}${f.unit || ''}`;
};
const fmtDate = (d) => (d ? new Date(d + 'T12:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }) : '');

function Spark({ series }) {
  if (!series || series.length < 2) return null;
  const vals = series.map((p) => p.value);
  const min = Math.min(...vals), max = Math.max(...vals), span = max - min || 1;
  const w = 120, h = 32;
  const pts = vals.map((v, i) => `${(i / (vals.length - 1)) * w},${h - 3 - ((v - min) / span) * (h - 6)}`).join(' ');
  const upTrend = vals[vals.length - 1] >= vals[0];
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-[120px] h-8" aria-hidden="true">
      <polyline fill="none" stroke={upTrend ? '#16a34a' : '#dc2626'} strokeWidth="2" points={pts} />
      <circle cx={w} cy={h - 3 - ((vals[vals.length - 1] - min) / span) * (h - 6)} r="2.5" fill={upTrend ? '#16a34a' : '#dc2626'} />
    </svg>
  );
}

export default function MarketData({ data, currency }) {
  const [open, setOpen] = useState(null);
  if (!data?.feeds?.length) return null;
  const asOf = data.as_of ? new Date(data.as_of).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

  return (
    <div className="card-light mt-5">
      <div className="flex items-end justify-between flex-wrap gap-2 mb-4">
        <div>
          <SectionLabel>The data behind this outlook</SectionLabel>
          <p className="text-slate-500 text-sm">Live figures we hand the model before it writes the {currency} outlook. Tap a row to see why it matters.</p>
        </div>
        <p className="text-xs text-slate-400">Fetched {asOf} · refreshed hourly</p>
      </div>

      <div className="divide-y divide-slate-100">
        {data.feeds.map((f) => {
          const isOpen = open === f.key;
          const chg = f.change_pct;
          return (
            <div key={f.key}>
              <button onClick={() => setOpen(isOpen ? null : f.key)} className="w-full flex items-center gap-3 py-3 text-left hover:bg-slate-50 -mx-2 px-2 rounded-lg transition" aria-expanded={isOpen}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{f.label}</p>
                  <p className="text-xs text-slate-400">{f.source}{f.date ? ` · ${fmtDate(f.date)}` : ''}</p>
                </div>
                {f.series && <Spark series={f.series} />}
                <div className="text-right w-28 flex-shrink-0">
                  <p className="font-mono font-semibold text-slate-900">{fmtVal(f)}</p>
                  {chg != null && <p className={`font-mono text-xs ${chg >= 0 ? 'text-green-600' : 'text-red-600'}`}>{chg >= 0 ? '▲' : '▼'} {Math.abs(chg).toFixed(2)}%</p>}
                </div>
                <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>
              {isOpen && (
                <div className="pb-4 pl-2 pr-8">
                  <p className="text-sm text-slate-600 leading-relaxed">{f.why}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-400">
        <a href="https://www.bankofcanada.ca/rates/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-500 inline-flex items-center gap-1">Bank of Canada <ExternalLink className="w-3 h-3" /></a>
        <a href="https://fred.stlouisfed.org/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-500 inline-flex items-center gap-1">FRED — Federal Reserve data <ExternalLink className="w-3 h-3" /></a>
        <span>Public data, no adjustments.</span>
      </div>
    </div>
  );
}
